import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatures } from '../contexts/FeatureContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { UserImpersonationSelector } from './UserImpersonationSelector';
import { ImportJobsIndicator } from './ImportJobsIndicator';
import { ClipboardList, Building2, LogOut, LayoutDashboard, UserCheck, Shield, Briefcase, Menu, X, Map, Route, BarChart3, Moon, Sun, Wrench, CalendarClock, Users, FileCheck, FileText, ChevronDown, ChevronRight, Database, Settings, Package, Layers, BookOpen, Activity, MessageCircle, TrendingUp, MessageSquare, DollarSign, Sparkles } from 'lucide-react';
import { 
  isAdmin, 
  isSmeOrAbove, 
  isSurveyorOrAbove, 
  isTechnicianOrAbove, 
  isPlannerOrAbove,
  isSuperAdmin,
  getRoleDisplayName 
} from '../lib/permissions';
import { workOrdersApi } from '../lib/api';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { features } = useFeatures();
  const { isImpersonating, targetUser, canImpersonate } = useImpersonation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    'home': true,  // Home/Dashboard always visible
    'admin': false,
    'finance': false,  // Planning Hub collapsed by default
    'fca': false,
    'iwms': false,
    'ops': false   // Operations Hub collapsed by default (like other modules)
  });
  const sidebarRef = useRef(null);
  const sidebarScrollPos = useRef(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [activeWorkOrdersCount, setActiveWorkOrdersCount] = useState(0);

  // Fetch counts for navigation badges
  const fetchCounts = useCallback(async () => {
    if (!user || !features.iwms_enabled) return;
    try {
      const [unreadData, activeData] = await Promise.all([
        workOrdersApi.getUnreadMessagesCount(),
        workOrdersApi.getActiveCount()
      ]);
      setUnreadMessagesCount(unreadData.unread_count || 0);
      setActiveWorkOrdersCount(activeData.active_count || 0);
    } catch (error) {
      console.error('Failed to fetch navigation counts:', error);
    }
  }, [user, features.iwms_enabled]);

  // Fetch counts on mount and periodically
  useEffect(() => {
    fetchCounts();
    // Poll every 60 seconds for updates
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Refetch when navigating away from work orders or messaging
  useEffect(() => {
    if (!location.pathname.startsWith('/work-orders') && !location.pathname.startsWith('/messaging')) {
      fetchCounts();
    }
  }, [location.pathname, fetchCounts]);

  // Refresh counts when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCounts();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchCounts]);

  // Listen for work order changes (delete, create, status change)
  useEffect(() => {
    const handleWorkOrderChange = () => {
      fetchCounts();
    };
    
    window.addEventListener('workorder-changed', handleWorkOrderChange);
    return () => window.removeEventListener('workorder-changed', handleWorkOrderChange);
  }, [fetchCounts]);

  // Preserve sidebar scroll position across route changes
  useEffect(() => {
    // Use requestAnimationFrame to restore after React finishes rendering
    const restoreScroll = () => {
      if (sidebarRef.current && sidebarScrollPos.current > 0) {
        sidebarRef.current.scrollTop = sidebarScrollPos.current;
      }
    };
    
    // Small delay to ensure DOM is updated
    requestAnimationFrame(() => {
      requestAnimationFrame(restoreScroll);
    });
  }, [location.pathname]);

  const handleSidebarScroll = () => {
    if (sidebarRef.current) {
      sidebarScrollPos.current = sidebarRef.current.scrollTop;
    }
  };

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Build navigation structure with sections
  const navigationSections = [];
  const userRole = user?.role;

  // HOME - always at the very top, always visible
  navigationSections.push({
    name: 'Home',
    key: 'home',
    alwaysOpen: true,
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    ]
  });

  // ADMIN Section - contains both system admin and client/facility management
  // Level 3+ (Field Surveyor and above) can see Clients, Facilities, Floor Plans
  // Level 6+ (Admin and above) can also see System Admin items
  if (isSurveyorOrAbove(userRole)) {
    const adminItems = [];
    
    // System Admin items - Level 6+ (Admin and above)
    if (isAdmin(userRole)) {
      adminItems.push(
        { name: 'System Admin', href: '/organization', icon: Database },
        { name: 'System Docs', href: '/system-docs', icon: BookOpen }
      );
      
      // Health Dashboard - Super Admin and Founder only
      if (isSuperAdmin(userRole)) {
        adminItems.push({ name: 'DB Health', href: '/health-dashboard', icon: Activity });
      }
    }
    
    // Client/Facility management - Level 3+ (Field Surveyor and above)
    adminItems.push(
      { name: 'Clients', href: '/clients', icon: Briefcase },
      { name: 'Facilities', href: '/facilities', icon: Building2 },
      { name: 'Floor Plans', href: '/floor-plans', icon: Layers }
    );
    
    navigationSections.push({
      name: 'Admin',
      key: 'admin',
      items: adminItems
    });
  }
  
  // PLANNING HUB Section - Level 6+ (Admin and above)
  // Collapsible hub with sub-items matching other hubs
  if (isAdmin(userRole)) {
    navigationSections.push({
      name: 'Planning Hub',
      key: 'finance',
      items: [
        { name: 'AI Dashboard', href: '/ai-dashboard', icon: Sparkles, isNew: true },
        { name: 'Executive Summary', href: '/finance-hub?tab=executive', icon: TrendingUp },
        { name: 'Capital Planning', href: '/finance-hub?tab=capital', icon: DollarSign },
        { name: 'Maintenance Costs', href: '/finance-hub?tab=maintenance', icon: Wrench },
        { name: 'Vendor Performance', href: '/finance-hub?tab=vendor', icon: Users },
        { name: 'Scorecard', href: '/finance-hub?tab=scorecard', icon: BarChart3 },
        { name: 'Replacement Scenarios', href: '/finance-hub?tab=scenarios', icon: TrendingUp, isNew: true },
        { name: 'Export Reports', href: '/finance-hub?tab=export', icon: FileText }
      ]
    });
  }

  // Assessment Hub Section - Level 3+ (Field Surveyor and above)
  if (features.fca_enabled !== false && isSurveyorOrAbove(userRole)) {
    const fcaItems = [];
    // FCA Admin at top of FCA section (admin only - Level 6+)
    if (isAdmin(userRole)) {
      fcaItems.push({ name: 'Assessment Admin', href: '/admin', icon: Settings });
    }
    // Review Queue in FCA section (SME and above - Level 4+)
    if (isSmeOrAbove(userRole)) {
      fcaItems.push({ name: 'Review Queue', href: '/review-queue', icon: UserCheck });
    }
    fcaItems.push(
      { name: 'Assessments', href: '/assessments', icon: ClipboardList },
      { name: 'Portfolio Map', href: '/portfolio-map', icon: Map },
      { name: 'Inspection Planner', href: '/inspection-planner', icon: Route },
      { name: 'Regional Analysis', href: '/regional-analysis', icon: BarChart3 },
    );
    navigationSections.push({
      name: 'Assessment Hub',
      key: 'fca',
      items: fcaItems
    });
  }

  // Work Management Hub Section - Different roles see different items
  if (features.iwms_enabled && isTechnicianOrAbove(userRole)) {
    const iwmsItems = [];
    // IWMS Admin at top of IWMS section (admin only - Level 6+)
    if (isAdmin(userRole)) {
      iwmsItems.push({ name: 'WMS Admin', href: '/iwms-admin', icon: Settings });
    }
    // Work Orders - Level 2+ (Technician and above) - with badge for active count
    iwmsItems.push({ name: 'Work Orders', href: '/work-orders', icon: Wrench, badge: activeWorkOrdersCount });
    // Messaging - Level 2+ (Technician and above) - with badge for unread messages
    iwmsItems.push({ name: 'Messaging', href: '/messaging', icon: MessageSquare, badge: unreadMessagesCount });
    // Assets - Level 3+ (Field Surveyor and above)
    if (isSurveyorOrAbove(userRole)) {
      iwmsItems.push({ name: 'Assets', href: '/iwms-assets', icon: Package });
    }
    // PM Schedules, Warranties, Leases - Level 3+ (Field Surveyor and above)
    if (isSurveyorOrAbove(userRole)) {
      iwmsItems.push(
        { name: 'PM Schedules', href: '/pm-schedules', icon: CalendarClock },
        { name: 'Warranties', href: '/warranties', icon: FileCheck },
        { name: 'Leases', href: '/leases', icon: FileText },
      );
    }
    // Vendors - Level 5+ (Planner and above)
    if (isPlannerOrAbove(userRole)) {
      iwmsItems.push({ name: 'Vendors', href: '/vendors', icon: Users });
    }
    navigationSections.push({
      name: 'Work Management Hub',
      key: 'iwms',
      items: iwmsItems
    });
  }

  // Operations Hub Section - Scheduling, Dispatch, and Field Operations
  // Level 5+ (Planner/Supervisor and above) for most items
  if (features.iwms_enabled && isTechnicianOrAbove(userRole)) {
    const opsItems = [];
    
    // Scheduler - Level 5+ (Planner and above)
    if (isPlannerOrAbove(userRole)) {
      opsItems.push({ name: 'Scheduler', href: '/scheduler', icon: CalendarClock });
      opsItems.push({ name: 'Live Map', href: '/live-map', icon: Activity });
      opsItems.push({ name: 'Work Order Map', href: '/work-order-map', icon: Route });
      opsItems.push({ name: 'GIS Dashboard', href: '/gis-map', icon: Map });
    }
    
    // Technician Mobile - Level 2+ (Technician and above)
    opsItems.push({ name: 'My Schedule', href: '/technician', icon: ClipboardList });
    
    if (opsItems.length > 0) {
      navigationSections.push({
        name: 'Operations Hub',
        key: 'ops',
        items: opsItems
      });
    }
  }

  const handleNavClick = (e) => {
    // Capture current sidebar scroll position before navigation
    const currentScroll = sidebarRef.current?.scrollTop || 0;
    sidebarScrollPos.current = currentScroll;
    
    // Only close menu on mobile (don't scroll main content)
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex transition-colors duration-200" data-testid="app-layout">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 text-white z-40 flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-md hover:bg-slate-800 transition-colors"
          data-testid="mobile-menu-button"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="flex items-center gap-2">
          {/* FMiQ-One Logo for mobile header */}
          <img 
            src="/images/fmiq-one-logo.png" 
            alt="FMiQ-One" 
            className="h-10" 
          />
        </div>
        {/* Dark mode toggle for mobile header */}
        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-md hover:bg-slate-800 transition-colors"
          data-testid="dark-mode-toggle-mobile"
          title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {darkMode ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-slate-300" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop always visible, Mobile slides in */}
      <aside 
        ref={sidebarRef}
        onScroll={handleSidebarScroll}
        className={`
          fixed inset-y-0 left-0 z-50
          w-64 text-white
          transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          overflow-y-auto overscroll-contain
          md:sticky md:top-0 md:h-screen
        `}
        style={{ 
          WebkitOverflowScrolling: 'touch', 
          scrollBehavior: 'auto',
          backgroundColor: '#0F172A' /* FMiQ Deep Navy */
        }}
        data-testid="sidebar"
      >
        <div className="flex flex-col min-h-full">
          {/* Close button for mobile */}
          <div className="md:hidden flex justify-end p-2 sticky top-0 z-10" style={{ backgroundColor: '#0F172A' }}>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-md hover:bg-slate-800 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-4 border-b border-slate-700/50 flex-shrink-0">
            {/* FMiQ Logo Container */}
            <div className="relative group flex justify-center">
              {/* Animated glow behind logo - Using brand Primary Blue */}
              <div className="absolute inset-0 bg-gradient-to-b from-blue-500/20 via-blue-400/10 to-amber-500/10 rounded-xl blur-xl opacity-50 group-hover:opacity-80 transition-all duration-500 animate-pulse" style={{animationDuration: '3s'}} />
              
              {/* PNG Logo - FMiQ-One for dark sidebar background */}
              <img 
                src="/images/fmiq-one-logo.png" 
                alt="FMiQ-One" 
                className="relative w-full max-w-[140px] mx-auto group-hover:scale-105 transition-transform duration-300" 
              />
            </div>
          </div>

          <nav className="p-4 space-y-2 flex-shrink-0" data-testid="main-navigation">
            {navigationSections.map((section) => {
              // Direct link section (no sub-items, just a single link)
              const isDirectLink = section.href && (!section.items || section.items.length === 0);
              
              if (isDirectLink) {
                const Icon = section.icon;
                const isActive = location.pathname === section.href || location.pathname.startsWith(section.href);
                return (
                  <div key={section.key} className="mb-2">
                    <Link
                      to={section.href}
                      onClick={handleNavClick}
                      data-testid={`nav-link-${section.name.toLowerCase().replace(/ /g, '-')}`}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-all duration-200 ${
                        isActive
                          ? 'bg-sky-500 text-white shadow-sm'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      {Icon && <Icon size={18} />}
                      <span className="font-medium text-sm">{section.name}</span>
                    </Link>
                  </div>
                );
              }
              
              // Expandable section with sub-items
              return (
                <div key={section.key} className="mb-2">
                  {/* Section Header - clickable to collapse/expand */}
                  {!section.alwaysOpen && (
                    <button
                      onClick={() => toggleSection(section.key)}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-300"
                    >
                      <span>{section.name}</span>
                      {expandedSections[section.key] ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  
                  {/* Section Items */}
                  {(section.alwaysOpen || expandedSections[section.key]) && (
                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.href;
                        const hasBadge = item.badge && item.badge > 0;
                        const isNewFeature = item.isNew;
                        
                        return (
                          <div key={item.name} className="relative">
                            <Link
                              to={item.href}
                              onClick={handleNavClick}
                              onMouseDown={(e) => {
                                // Capture scroll position before any browser scroll behavior
                                if (sidebarRef.current) {
                                  sidebarScrollPos.current = sidebarRef.current.scrollTop;
                                }
                              }}
                              tabIndex={-1}
                              data-testid={`nav-link-${item.name.toLowerCase().replace(/ /g, '-')}`}
                              className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-all duration-200 ${
                                isActive
                                  ? 'bg-sky-500 text-white shadow-sm'
                                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                              }`}
                            >
                              <Icon size={18} />
                              <span className="font-medium text-sm flex-1">{item.name}</span>
                              {isNewFeature && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-teal-500 text-white">
                                  NEW
                                </span>
                              )}
                              {hasBadge && (
                                <span
                                  className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-red-500 text-white"
                                  data-testid={`nav-badge-${item.name.toLowerCase().replace(' ', '-')}`}
                                  title={item.href === '/work-orders' ? 'Open work orders' : item.href === '/messaging' ? 'Unread messages' : ''}
                                >
                                  {item.badge > 99 ? '99+' : item.badge}
                                </span>
                              )}
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Spacer to push footer to bottom on tall screens */}
          <div className="flex-grow" />

          <div className="p-4 border-t border-slate-800 flex-shrink-0">
            <div className="mb-3" data-testid="user-info">
              <p className="text-sm font-semibold text-white">{user?.full_name}</p>
              <p className="text-xs text-slate-400 uppercase tracking-wider">{getRoleDisplayName(user?.role)}</p>
            </div>
            
            {/* User Impersonation Selector - Super Admin/Founder only */}
            {canImpersonate && !isImpersonating && (
              <div className="mb-2">
                <UserImpersonationSelector />
              </div>
            )}
            
            {/* Import Jobs Indicator - shows active background imports */}
            <div className="mb-2 flex justify-start">
              <ImportJobsIndicator />
            </div>
            
            {/* Dark Mode Toggle - Desktop */}
            <Button
              onClick={toggleDarkMode}
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800 mb-2"
              data-testid="dark-mode-toggle"
            >
              {darkMode ? (
                <>
                  <Sun size={18} className="mr-2 text-yellow-400" />
                  Light Mode
                </>
              ) : (
                <>
                  <Moon size={18} className="mr-2" />
                  Dark Mode
                </>
              )}
            </Button>
            
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="logout-button"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
            <div className="mt-4 pt-4 border-t border-slate-800 flex justify-center">
              <img src="/sba-badge.png" alt="Service-Disabled Veteran-Owned Certified" className="w-20 opacity-70 hover:opacity-100 transition-opacity" />
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content - add top padding when impersonating */}
      <main 
        className={`flex-1 overflow-auto bg-slate-50 dark:bg-slate-900 transition-colors duration-200 ${
          isImpersonating ? 'pt-24 md:pt-10' : 'pt-14 md:pt-0'
        }`}
        data-testid="main-content"
      >
        {children}
      </main>
    </div>
  );
};

export default Layout;
